import { inject, Injectable } from '@angular/core';
import { ApiResponseModel, ApiService } from '@solcre-org/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment.local';
import { PortTariffLineModel } from '../models/port-tariff-line.model';
import { PortTariffModel } from '../models/port-tariff.model';

export interface PortTariffLineReorderItem {
  id: string | number;
  sortOrder: number;
}

@Injectable({
  providedIn: 'root',
})
export class PortTariffService {
  private apiService: ApiService = inject(ApiService);

  public fetchPortTariffs(portId: string): Observable<PortTariffModel[]> {
    return this.apiService.fetchData(this.portTariffsPath(portId)).pipe(
      map((response: ApiResponseModel) => new PortTariffModel().fromJSONList(this.extractCollection(response.data)))
    );
  }

  public fetchPortTariff(portTariffId: string): Observable<PortTariffModel> {
    return this.apiService.fetchData(this.portTariffPath(portTariffId)).pipe(
      map((response: ApiResponseModel) => new PortTariffModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public createPortTariff(portId: string, payload: { berthingType: string }): Observable<PortTariffModel> {
    return this.apiService.createObj(this.portTariffsPath(portId), payload).pipe(
      map((response: ApiResponseModel) => new PortTariffModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public updatePortTariff(
    portTariffId: string,
    payload: Partial<Pick<PortTariffModel, 'port' | 'berthingType'>>
  ): Observable<PortTariffModel> {
    return this.apiService.partialUpdateObj(
      environment.API.PORT_TARIFFS_COLLECTION_URI,
      portTariffId,
      payload,
      false,
      { 'Content-Type': 'application/merge-patch+json' }
    ).pipe(
      map((response: ApiResponseModel) => new PortTariffModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public archivePortTariff(portTariffId: string, reason: string): Observable<PortTariffModel> {
    return this.apiService.createObj(this.portTariffArchivePath(portTariffId), { reason }).pipe(
      map((response: ApiResponseModel) => new PortTariffModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public duplicatePortTariff(portTariffId: string, berthingType: string): Observable<PortTariffModel> {
    return this.apiService.createObj(this.portTariffDuplicatePath(portTariffId), { berthingType }).pipe(
      map((response: ApiResponseModel) => new PortTariffModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public createPortTariffLine(
    tariffId: string | number,
    payload: Partial<PortTariffLineModel>
  ): Observable<PortTariffLineModel> {
    return this.apiService.createObj(environment.API.PORT_TARIFF_LINES_URI, {
      ...payload,
      tariff: this.tariffIri(tariffId),
    }).pipe(
      map((response: ApiResponseModel) => new PortTariffLineModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public updatePortTariffLine(
    lineId: string,
    payload: Partial<PortTariffLineModel>
  ): Observable<PortTariffLineModel> {
    return this.apiService.partialUpdateObj(
      environment.API.PORT_TARIFF_LINES_URI,
      lineId,
      payload,
      false,
      { 'Content-Type': 'application/merge-patch+json' }
    ).pipe(
      map((response: ApiResponseModel) => new PortTariffLineModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public archivePortTariffLine(lineId: string): Observable<PortTariffLineModel> {
    return this.apiService.createObj(this.portTariffLineArchivePath(lineId), {}).pipe(
      map((response: ApiResponseModel) => new PortTariffLineModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public reorderPortTariffLines(lines: PortTariffLineReorderItem[]): Observable<PortTariffLineModel[]> {
    return this.apiService.createObj(environment.API.PORT_TARIFF_LINE_REORDER_URI, { lines }).pipe(
      map((response: ApiResponseModel) => new PortTariffLineModel().fromJSONList(this.extractCollection(response.data)))
    );
  }

  private portTariffsPath(portId: string): string {
    return environment.API.PORT_TARIFFS_URI.replace(':id', portId);
  }

  private portTariffPath(portTariffId: string): string {
    return `${environment.API.PORT_TARIFFS_COLLECTION_URI}/${portTariffId}`;
  }

  private portTariffArchivePath(portTariffId: string): string {
    return `${this.portTariffPath(portTariffId)}/archive`;
  }

  private portTariffDuplicatePath(portTariffId: string): string {
    return `${this.portTariffPath(portTariffId)}/duplicate`;
  }

  private portTariffLinePath(lineId: string): string {
    return `${environment.API.PORT_TARIFF_LINES_URI}/${lineId}`;
  }

  private portTariffLineArchivePath(lineId: string): string {
    return `${this.portTariffLinePath(lineId)}/archive`;
  }

  private tariffIri(portTariffId: string | number): string {
    return `/api/port-tariffs/${portTariffId}`;
  }

  private extractObject(data: any): any {
    if (data && typeof data === 'object' && 'data' in data) {
      return data.data;
    }

    return data;
  }

  private extractCollection(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.items)) {
      return data.items;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.['hydra:member'])) {
      return data['hydra:member'];
    }

    return [];
  }
}
